<template>
  <div class="head-to-head-results">
    <div class="results-header">
      <h3>Match Results</h3>
      <span class="results-count">{{ matches.length }} match{{ matches.length !== 1 ? 'es' : '' }}</span>
    </div>

    <div class="match-results-list">
      <div class="match-row" v-for="match in matches" :key="match.id">
        <div class="match-info">
          <span class="match-number">{{ match.matchNumber || match.name || 'Match' }}</span>
          <span class="match-status" :class="getStatusClass(match.status)">{{ match.status || 'Not Started' }}</span>
        </div>

                 <div class="participants-row">
           <div class="participant" :class="{ 'winner': isWinner(match, 'team1') }">
             <span class="participant-name">
               {{ getMatchParticipantName(match.participants[0]) }}
               <span class="winner-indicator" v-if="isWinner(match, 'team1')">✓</span>
             </span>
             <span class="participant-result" :class="getResultClass(match.participants[0], match, 0)">{{ getParticipantResult(match.participants[0], match, 0) }}</span>
           </div>

           <div class="vs-separator">vs</div>

           <div class="participant" :class="{ 'winner': isWinner(match, 'team2') }">
             <span class="participant-name">
               {{ getMatchParticipantName(match.participants[1]) }}
               <span class="winner-indicator" v-if="isWinner(match, 'team2')">✓</span>
             </span>
             <span class="participant-result" :class="getResultClass(match.participants[1], match, 1)">{{ getParticipantResult(match.participants[1], match, 1) }}</span>
           </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'CustomHeadToHeadResults',
  props: {
    matches: {
      type: Array,
      required: true,
    },
    getMatchParticipantName: {
      type: Function,
      required: true,
    },
    scoringType: {
      type: String,
      default: 'score'
    }
  },
  methods: {
    getParticipantResult(participant, match, participantIndex) {
      if (!participant) return '';

      // For result-based scoring, show W/L/D based on match winner
      if (this.scoringType === 'result') {
        if (match && match.winner !== null && match.winner !== undefined) {
          const winner = match.winner.toString();

          // Handle draw case
          if (winner === 'draw') {
            return 'D';
          }

          // Handle win/loss case
          const currentIndex = participantIndex.toString();
          if (winner === currentIndex) {
            return 'W';
          } else {
            return 'L';
          }
        }
        return '';
      }

      // For time-based scoring, show time
      if (this.scoringType === 'time' && participant.time) {
        return participant.time;
      }

      // For regular scoring, show score
      if (participant.score !== null && participant.score !== undefined) {
        return participant.score;
      }

      return '';
    },

    isWinner(match, teamKey) {
      if (!match.participants || match.participants.length < 2) return false;

      if (this.scoringType === 'result') {
        // For result-based scoring, use the winner field
        if (match.winner !== null && match.winner !== undefined) {
          const winner = match.winner.toString();

          // No winner styling for draws
          if (winner === 'draw') {
            return false;
          }

          if (teamKey === 'team1') {
            return winner === '0';
          } else if (teamKey === 'team2') {
            return winner === '1';
          }
        }
        return false;
      }

      const participant1 = match.participants[0];
      const participant2 = match.participants[1];

      if (this.scoringType === 'time') {
        // For time-based scoring, fastest time wins
        const team1Time = this.parseTime(participant1.time);
        const team2Time = this.parseTime(participant2.time);

        if (team1Time === null || team2Time === null) return false;

        if (teamKey === 'team1') {
          return team1Time < team2Time;
        } else {
          return team2Time < team1Time;
        }
      } else {
        // For score-based, higher score wins
        const team1Score = parseFloat(participant1.score) || 0;
        const team2Score = parseFloat(participant2.score) || 0;

        if (team1Score === 0 && team2Score === 0) return false;

        if (teamKey === 'team1') {
          return team1Score > team2Score;
        } else {
          return team2Score > team1Score;
        }
      }
    },

    parseTime(timeStr) {
      if (!timeStr) return null;

      // Handle formats like "1:30", "2:15.50", etc.
      const parts = timeStr.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseFloat(parts[1]) || 0;
        return minutes * 60 + seconds;
      }

      // Handle seconds only
      return parseFloat(timeStr) || null;
    },

    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
      if (statusLower.includes('progress') || statusLower.includes('live')) return 'live';
      if (statusLower.includes('not started') || statusLower.includes('scheduled')) return 'scheduled';
      return 'default';
    },

    getResultClass(participant, match, participantIndex) {
      if (this.scoringType !== 'result') return '';

      if (match && match.winner !== null && match.winner !== undefined) {
        const winner = match.winner.toString();

        // Handle draw case
        if (winner === 'draw') {
          return 'result-draw';
        }

        // Handle win/loss case
        const currentIndex = participantIndex.toString();
        if (winner === currentIndex) {
          return 'result-win';
        } else {
          return 'result-loss';
        }
      }

      return '';
    }
  }
});
</script>

<style lang="scss" scoped>
$card-bg: #ffffff;
$text-primary: #111827;
$text-secondary: #6b7280;
$text-light: #9ca3af;
$accent-color: #3b82f6;
$success-color: #10b981;
$warning-color: #f59e0b;
$error-color: #ef4444;

$border-radius: 8px;
$shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);

.head-to-head-results {
  background: $card-bg;
  border-radius: $border-radius;
  box-shadow: $shadow-sm;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.results-header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }

  .results-count {
    font-size: 0.8rem;
    color: $text-secondary;
    background: #f3f4f6;
    padding: 0.25rem 0.6rem;
    border-radius: 8px;
    font-weight: 500;
  }
}

.match-results-list {
  display: flex;
  flex-direction: column;
}

.match-row {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafafa;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
  }
}

.match-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  .match-number {
    font-weight: 600;
    color: $text-primary;
    font-size: 0.875rem;
  }

  .match-status {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &.completed {
      background: #f0f9ff;
      color: #0369a1;
    }

    &.live {
      background: #fef3c7;
      color: #92400e;
    }

    &.scheduled {
      background: #f0f9ff;
      color: #0369a1;
    }

    &.default {
      background: #f3f4f6;
      color: $text-secondary;
    }
  }
}

.participants-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: center;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
}

.participant {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f3f4f6;
  transition: all 0.2s ease;

  &.winner {
    background: #f0f9ff;
    border-color: #3b82f6;
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);

    .participant-name {
      color: #1e40af;
      font-weight: 600;
    }
  }

  .participant-name {
    font-weight: 500;
    color: $text-primary;
    font-size: 0.875rem;
    flex: 1;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    @media (max-width: 480px) {
      font-size: 0.8rem;
    }

    .winner-indicator {
      color: #10b981;
      font-weight: 700;
      font-size: 0.75rem;
      background: #dcfce7;
      padding: 0.1rem 0.3rem;
      border-radius: 50%;
      line-height: 1;
      min-width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 480px) {
        font-size: 0.7rem;
        min-width: 16px;
        height: 16px;
      }
    }
  }

  .participant-result {
    font-weight: 600;
    color: $text-secondary;
    font-size: 0.875rem;
    min-width: 40px;
    text-align: right;

    @media (max-width: 480px) {
      font-size: 0.8rem;
      min-width: 35px;
    }

    &.result-win {
      background: $success-color;
      color: white;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 700;
      text-align: center;
      min-width: 24px;
    }

    &.result-loss {
      background: #dc2626;
      color: white;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 700;
      text-align: center;
      min-width: 24px;
    }

    &.result-draw {
      background: #6b7280;
      color: white;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 700;
      text-align: center;
      min-width: 24px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.6rem;
  }
}

.vs-separator {
  font-weight: 600;
  color: $text-light;
  font-size: 0.7rem;
  text-align: center;
  padding: 0.3rem;
  background: #f8f9fa;
  border-radius: 4px;
  min-width: 28px;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 0.25rem;
    min-width: 24px;
  }
}
</style>
